import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import { useOverlay } from './overlayContext';
import type { OverlayAction } from './overlay.types';

export interface OverlayFooterProps {
  /** The main action (Save / Submit / Confirm), on the far right. */
  primary?: OverlayAction;
  /** More actions between Cancel and the primary action. */
  secondary?: OverlayAction[];
  /** Cancel. Defaults to closing the enclosing overlay; `null` hides it. */
  onCancel?: (() => void) | null;
  cancelLabel?: string;
  /** Destructive action on the far left (edit forms). */
  onDelete?: () => void | Promise<void>;
  deleteLabel?: string;
  /** Free content on the left, e.g. "Last saved 2 min ago". */
  start?: ReactNode;
  /** Disables every action and shows the primary spinner. Defaults to the overlay's busy state. */
  busy?: boolean;
}

function ActionButton({ action, busy, fallbackVariant }: { action: OverlayAction; busy: boolean; fallbackVariant: OverlayAction['variant'] }) {
  const loading = action.loading ?? false;
  return (
    <Button
      type={action.type ?? 'button'}
      variant={action.variant ?? fallbackVariant}
      color={action.tone ?? 'primary'}
      onClick={action.onClick ? () => void action.onClick?.() : undefined}
      disabled={busy || loading || action.disabled}
      startIcon={loading ? <CircularProgress size={16} color="inherit" /> : action.startIcon}
      autoFocus={action.autoFocus}
      aria-label={action['aria-label']}
    >
      {action.label}
    </Button>
  );
}

/**
 * Standard action bar: [Delete | start] ........ [Cancel] [secondary…] [Primary].
 * Order and emphasis are fixed so every overlay reads the same way.
 */
export function OverlayFooter({
  primary,
  secondary = [],
  onCancel,
  cancelLabel = 'Cancel',
  onDelete,
  deleteLabel = 'Delete',
  start,
  busy,
}: OverlayFooterProps) {
  const overlay = useOverlay();
  const isBusy = busy ?? overlay?.busy ?? false;
  const cancel = onCancel === undefined ? (overlay ? () => overlay.requestClose('cancel') : undefined) : onCancel;

  return (
    <Stack
      direction="row"
      spacing={1}
      sx={{
        alignItems: 'center',
        px: { xs: 1, sm: 1.5 },
        py: 1,
        borderTop: 1,
        borderColor: 'divider',
        flexShrink: 0,
      }}
    >
      {onDelete ? (
        <Button
          color="error"
          size='small'
          startIcon={<DeleteOutlineIcon />}
          onClick={() => void onDelete()}
          disabled={isBusy}
        >
          {deleteLabel}
        </Button>
      ) : null}
      {start}
      <Box sx={{ flex: 1 }} />
      {cancel ? (
        <Button color="inherit" variant='outlined' size='small' onClick={cancel} disabled={isBusy}>
          {cancelLabel}
        </Button>
      ) : null}
      {secondary.map((action, i) => (
        <ActionButton key={i} action={action} busy={isBusy} fallbackVariant="outlined" />
      ))}
      {primary ? (
        <ActionButton
          action={{ ...primary, loading: primary.loading ?? (primary.type === 'submit' && isBusy) }}
          busy={isBusy}
          fallbackVariant="contained"
        />
      ) : null}
    </Stack>
  );
}
